import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useBanners } from "../hooks/useBanners"
import {
  addDismissedBanner,
  getDismissedBanners,
} from "../helpers/dismissedBanners"
import type { TBanner } from "../types"
import { Banner } from "../models/banner"

export type HeroBannerContextValue = {
  activeBanner: TBanner | null
  dismiss: (uid: string) => void
  isLoading: boolean
}

export const HeroBannerContext = createContext<HeroBannerContextValue | null>(
  null,
)

type HeroBannerProviderProps = {
  children: ReactNode
}

export const HeroBannerProvider = ({ children }: HeroBannerProviderProps) => {
  const { data: banner, loading: isLoading } = useBanners()
  const [dismissed, setDismissed] = useState<string[]>([])

  useEffect(() => {
    setDismissed(getDismissedBanners())
  }, [])

  const dismiss = useCallback((uid: string) => {
    const next = addDismissedBanner(uid)
    setDismissed(next)
  }, [])

  const activeBanner = useMemo<Banner | null>(() => {
    if (!banner) return null
    if (dismissed.includes(banner.uid)) return null
    const now = Date.now()
    const expiryMs = banner.expiry ? new Date(banner.expiry).getTime() : NaN
    return new Banner({
      ...banner,
      expiry: Number.isFinite(expiryMs)
        ? expiryMs > now
          ? banner.expiry
          : ""
        : banner.expiry,
    })
  }, [banner, dismissed])

  const value = useMemo<HeroBannerContextValue>(
    () => ({
      activeBanner,
      dismiss,
      isLoading,
    }),
    [activeBanner, dismiss, isLoading],
  )

  return (
    <HeroBannerContext.Provider value={value}>
      {children}
    </HeroBannerContext.Provider>
  )
}

export const useHeroBanner = (): HeroBannerContextValue => {
  const context = useContext(HeroBannerContext)
  if (!context) {
    throw new Error("useHeroBanner must be used within a HeroBannerProvider")
  }
  return context
}
